import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useMyOrgs } from '../data/entities';
import { GUIDE_TITLE, GUIDE_LEAD, GUIDE_CHAPTERS, GUIDE_FIGURES } from '../data/onboardingGuide';
import { platformFree } from '../data/plans';
import PitchButton from '../components/PitchButton';

// /guide — the web edition of the onboarding guidebook ("Map, then
// optimize"). Visitor-safe: the chapters come from data/onboardingGuide.js
// (generated from backend/shared/onboardingGuide.js), the printed book is
// docs/nventr-onboarding.md, and NoVA's `start` pitch chapter speaks the
// same four moves. Section ids match the pitch nodes' `section` so a pitch
// step can scroll here. A signed-in member gets "Open Blueprint" for their
// organization; a visitor gets the account CTA. [[business-blueprint]]

// Figures (GUIDE_FIGURES, shared with NoVA's pitch) by chapter — under the
// lead — and by section — after the text. Each <figure id> is a pitch
// scroll target; files are the print figures the book build copies to
// public/guide/.
const chapterFigures = (chapterId) => GUIDE_FIGURES.filter((f) => f.chapter === chapterId && !f.section);
const sectionFigures = (sectionId) => GUIDE_FIGURES.filter((f) => f.section === sectionId);

function Figures({ list }) {
  if (!list.length) return null;
  return list.map((f) => (
    <figure className="cs-guide-figure" id={f.id} key={f.id} data-nova-note={`Figure: ${f.caption}`}>
      <img src={`/guide/${f.file}`} alt={f.caption} loading="lazy" />
      <figcaption>{f.caption}</figcaption>
    </figure>
  ));
}

function Body({ body }) {
  return body.map((b, i) => {
    if (typeof b === 'string') return <p key={i}>{b}</p>;
    if (b.bullets) return <ul key={i} className="cs-guide-bullets">{b.bullets.map((t, j) => <li key={j}>{t}</li>)}</ul>;
    if (b.worksheet) return <p key={i} className="cs-guide-worksheet"><span>Worksheet</span> {b.worksheet}</p>;
    return null;
  });
}

export default function Guide() {
  const { user } = useAuth();
  const orgs = useMyOrgs();
  const org = user && !user.isAnonymous && orgs?.length ? orgs[0] : null;
  return (
    <div className="cs-store cs-land cs-guide" data-nova-note="The onboarding guidebook, web edition: map the business, then optimize, build, track. Six chapters; the printed edition is the same text.">
      <header className="cs-store-hero">
        <span className="cs-mod-kicker">The Onboarding Guidebook</span>
        <h1>{GUIDE_TITLE}</h1>
        <p>{GUIDE_LEAD}</p>
        <div className="cs-land-cta-row cs-land-cta-center">
          {org
            ? <Link to={`/orgs/${org.id}/blueprint`} className="cs-btn cs-btn-primary">Open Blueprint</Link>
            : <Link to={platformFree.href} className="cs-btn cs-btn-primary">Create an account</Link>}
          <a href="/guide/nventr-onboarding-book.pdf" className="cs-btn cs-btn-outline" target="_blank" rel="noreferrer">Printed edition (PDF)</a>
        </div>
        <nav className="cs-guide-toc" aria-label="Chapters">
          {GUIDE_CHAPTERS.map((c) => <a key={c.id} href={`#${c.id}`}>{c.kicker} · {c.title}</a>)}
        </nav>
      </header>
      <PitchButton focus="start" hint="Or hear it from NoVA — map, optimize, build, track — and interrupt with questions." />

      {GUIDE_CHAPTERS.map((c) => (
        <section className="cs-store-section cs-guide-chapter" id={c.id} key={c.id}>
          <span className="cs-mod-kicker">{c.kicker}</span>
          <h2>{c.title}</h2>
          <p className="cs-store-note" style={{ maxWidth: 860 }}>{c.lead}</p>
          <Figures list={chapterFigures(c.id)} />
          {c.sections.map((s) => (
            <article className="cs-guide-section" id={s.id} key={s.id}>
              <h3>{s.title}</h3>
              <Body body={s.body} />
              <Figures list={sectionFigures(s.id)} />
            </article>
          ))}
        </section>
      ))}

      <section className="cs-store-section" id="begin">
        <h2>Where to click</h2>
        <p className="cs-store-note" style={{ maxWidth: 860 }}>
          Everything in this guide is one place in the console: <strong>Blueprint</strong>, in the Build section of your organization. Its tabs are the chapters — Map, Systems, Data, Processes, People, Initiatives, Impact — and the first time you open it NoVA offers to interview you. Say yes.
        </p>
        <div className="cs-land-cta-row">
          {org
            ? <Link to={`/orgs/${org.id}/blueprint`} className="cs-btn cs-btn-primary">Open Blueprint</Link>
            : <Link to={platformFree.href} className="cs-btn cs-btn-primary">Create an account — free, no card</Link>}
          <Link to="/pricing" className="cs-btn cs-btn-outline">Pricing</Link>
        </div>
      </section>
    </div>
  );
}
